/**
 * De hub: het startscherm ná het inloggen.
 *
 * Vanaf hier gaat alles: tegen de computer, met vrienden, of verder in een potje dat nog
 * loopt. Vroeger was dit het originele cd-hoofdmenu — acht knoppen waarvan er vijf "komt
 * later" zeiden — met de echte functies verstopt in een zij-lade, en een potje dat
 * zichzelf opende zodra je de pagina laadde. Nu is er één scherm dat toont wat er is en
 * niets doet zonder dat je erom vraagt.
 *
 * Gewone HTML op een canvas-achtergrond, net als het inlogscherm en om dezelfde reden:
 * lijsten en knoppen doet de browser beter dan het canvas. De originele menuplaat blijft
 * eronder staan als decor.
 */
import { KARAKTERS, type Ik } from '@catan/contracts';
import { asset, hasAsset } from './assets.js';
import { lopendePotjes, type LopendPotje } from './account.js';

const STIJL = `
#hub {
  position: absolute; inset: 0; z-index: 20; display: grid; place-items: center;
  background: rgba(12, 7, 2, 0.45);
}
#hub .kader {
  width: min(30rem, 94vw); max-height: 90vh; overflow: auto; padding: 1.3rem 1.5rem;
  background: rgba(23, 16, 10, 0.96); color: #f3e6cf;
  border: 2px solid #c9a227; border-radius: 6px;
  font-family: Georgia, 'Times New Roman', serif;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
}
#hub h2 { margin: 0; font-size: 1.25rem; color: #ffd257; }
#hub .wie {
  display: flex; align-items: baseline; gap: 0.6rem; margin: 0.15rem 0 1rem;
  font-size: 0.85rem; color: #d8c9a8;
}
#hub .wie strong { color: #f3e6cf; }
#hub .wie button {
  margin-left: auto; font-size: 0.75rem; padding: 0.2rem 0.5rem;
  background: #2c1608; color: #e3cfa3; border: 2px solid #7a5c33; border-radius: 4px;
  cursor: pointer; font-family: inherit;
}
#hub h3 {
  margin: 1.1rem 0 0.4rem; font-size: 0.9rem; color: #ffd257;
  border-bottom: 1px solid #6b5a3e; padding-bottom: 0.2rem;
}
#hub .keuzes { display: flex; gap: 0.7rem; }
#hub .keuze {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.45rem;
  padding: 0.9rem 0.6rem; cursor: pointer; font-family: inherit;
  background: #241708; color: #f3e6cf;
  border: 2px solid #7a5c33; border-radius: 6px; font-size: 0.95rem;
}
#hub .keuze:hover { border-color: #c9a227; background: #2e1d0a; }
#hub .keuze img { width: 65px; height: 54px; object-fit: cover; border-radius: 4px; }
#hub .keuze .sub { font-size: 0.72rem; color: #d8c9a8; }
#hub ul { list-style: none; margin: 0.3rem 0 0; padding: 0; }
#hub li { margin: 0.3rem 0; }
#hub li button {
  width: 100%; display: flex; align-items: center; gap: 0.6rem; text-align: left;
  padding: 0.4rem 0.6rem; cursor: pointer; font-family: inherit; font-size: 0.85rem;
  background: #241708; color: #f3e6cf; border: 2px solid #7a5c33; border-radius: 5px;
}
#hub li button:hover { border-color: #c9a227; }
#hub li img { width: 38px; height: 32px; object-fit: cover; border-radius: 3px; }
#hub li .naam { flex: 1; }
#hub li .soort { font-size: 0.72rem; color: #d8c9a8; }
#hub .melding { min-height: 1.1rem; margin: 0.8rem 0 0; font-size: 0.82rem; color: #ffb4a0; }
#hub .leeg { font-size: 0.82rem; color: #d8c9a8; }
`;

/** Zet tekst veilig in HTML. De namen komen van de server; vertrouwen is geen invoerfilter. */
const veilig = (tekst: string): string =>
  tekst.replace(/[&<>"']/g, (t) => `&#${t.charCodeAt(0)};`);

/** Het portret bij een computertegenstander, of niets als het een mens is. */
function portretVan(naam: string): string | undefined {
  const karakter = KARAKTERS.find((k) => k.naam === naam);
  if (karakter === undefined || !hasAsset('portretten', karakter.portret)) return undefined;
  return asset('portretten', karakter.portret).src;
}

export interface HubOpties {
  readonly ik: Ik;
  /** Tegen de computer spelen: door naar het opzetscherm. */
  readonly onSolo: () => void;
  /** Met vrienden spelen: de kamer- en vriendenlobby openen. */
  readonly onSamen: () => void;
  /** Een lopend potje verder spelen. */
  readonly onPotje: (id: string) => void;
  readonly onUitloggen: () => void;
}

/** Zet de hub neer. Geeft een functie terug die hem weghaalt. */
export function hubscherm(opties: HubOpties): () => void {
  if (document.getElementById('hub-stijl') === null) {
    const stijl = document.createElement('style');
    stijl.id = 'hub-stijl';
    stijl.textContent = STIJL;
    document.head.appendChild(stijl);
  }

  const laag = document.createElement('div');
  laag.id = 'hub';
  document.body.appendChild(laag);

  let lopend: LopendPotje[] = [];
  let geladen = false;
  let gesloten = false;

  const weg = (): void => {
    gesloten = true;
    laag.remove();
  };

  const teken = (): void => {
    const soloPortret = portretVan('Chi');
    laag.innerHTML = `
      <div class="kader">
        <h2>Het Kaartspel van Catan</h2>
        <p class="wie">Ingelogd als <strong>${veilig(opties.ik.gebruikersnaam)}</strong>
          <button id="hub-uitloggen">Uitloggen</button>
        </p>

        <h3>Nieuw potje</h3>
        <div class="keuzes">
          <button class="keuze" id="hub-solo">
            ${soloPortret === undefined ? '' : `<img src="${soloPortret}" alt="" />`}
            Tegen de computer
            <span class="sub">acht karakters, drie sterktes</span>
          </button>
          <button class="keuze" id="hub-samen">
            ${
              portretVan('Louis') === undefined
                ? ''
                : `<img src="${portretVan('Louis') ?? ''}" alt="" />`
            }
            Met vrienden
            <span class="sub">kamer maken of meedoen met een code</span>
          </button>
        </div>

        <h3>Verder spelen</h3>
        ${
          !geladen
            ? '<p class="leeg">Even kijken…</p>'
            : lopend.length === 0
              ? '<p class="leeg">Er loopt nu geen potje. Begin er hierboven een.</p>'
              : `<ul>${lopend
                  .map((p) => {
                    const portret =
                      p.tegenComputer === false ? undefined : portretVan(p.tegenstander);
                    return `<li>
                      <button data-potje-id="${veilig(p.id)}">
                        ${portret === undefined ? '' : `<img src="${portret}" alt="" />`}
                        <span class="naam">tegen ${veilig(p.tegenstander)}</span>
                        <span class="soort">${p.tegenComputer === false ? 'mens' : 'computer'}</span>
                      </button>
                    </li>`;
                  })
                  .join('')}</ul>`
        }

        <p class="melding" role="status" aria-live="polite"></p>
      </div>`;

    laag.querySelector('#hub-uitloggen')?.addEventListener('click', () => {
      weg();
      opties.onUitloggen();
    });
    laag.querySelector('#hub-solo')?.addEventListener('click', () => {
      weg();
      opties.onSolo();
    });
    laag.querySelector('#hub-samen')?.addEventListener('click', () => {
      // De hub blijft staan; de lobby komt eroverheen en bij sluiten sta je hier weer.
      opties.onSamen();
    });
    for (const knop of laag.querySelectorAll<HTMLElement>('[data-potje-id]')) {
      knop.addEventListener('click', () => {
        const id = knop.dataset['potjeId'];
        if (id === undefined) return;
        weg();
        opties.onPotje(id);
      });
    }
  };

  teken();
  // De lopende potjes komen van de server; het scherm staat er intussen al. Niets opent
  // zichzelf: de lijst is een aanbod, geen beslissing.
  void lopendePotjes().then((uit) => {
    if (gesloten) return;
    lopend = uit;
    geladen = true;
    teken();
  });
  return weg;
}
